/** Текстовые утилиты с учётом русской типографики. */

export const EM_DASH = '—';
export const EN_DASH = '–';
export const MINUS = '−';
export const NBSP = '\u00a0';
/** Символ-заместитель встроенного объекта (рисунка, формулы) в тексте абзаца. */
export const OBJ = '\ufffc';

const CYR_RE = /[Ѐ-ӿ]/;
const LAT_RE = /[A-Za-z]/;

export const isCyrillic = (ch: string): boolean => CYR_RE.test(ch);
export const isLatin = (ch: string): boolean => LAT_RE.test(ch);
export const hasCyrillic = (s: string): boolean => CYR_RE.test(s);
export const hasLetters = (s: string): boolean => /\p{L}/u.test(s);

/** Дефис, минус и все виды тире. */
export const DASH_CLASS = '\\-\\u2010-\\u2015\\u2212';

/**
 * Латинские буквы, внешне неотличимые от кириллических.
 * Такие подмены — частый след копирования из PDF, а «Антиплагиат»
 * считает их признаком попытки обхода проверки.
 */
export const LATIN_TO_CYRILLIC: Readonly<Record<string, string>> = {
  a: 'а', c: 'с', e: 'е', o: 'о', p: 'р', x: 'х', y: 'у',
  A: 'А', B: 'В', C: 'С', E: 'Е', H: 'Н', K: 'К', M: 'М', O: 'О', P: 'Р', T: 'Т', X: 'Х', Y: 'У',
};

export const CYRILLIC_TO_LATIN: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(LATIN_TO_CYRILLIC).map(([lat, cyr]) => [cyr, lat]),
);

/** Невидимые символы: пробелы нулевой ширины, соединители, BOM, мягкий перенос. */
export const INVISIBLE_RE = /[\u200b\u200c\u200d\u2060\ufeff\u00ad]/g;

/** Схлопывает все пробельные символы (включая неразрывные) в один пробел. */
export function collapseSpaces(s: string): string {
  return s.replace(/\s+/g, ' ').trim();
}

/** Нормализация для нечёткого сравнения заголовков и пунктов содержания. */
export function normalizeForCompare(s: string): string {
  return collapseSpaces(s.replace(INVISIBLE_RE, '').replace(/\ufffc/g, ''))
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[‐-―−]/g, '-')
    .replace(/[«»„“”"']/g, '"')
    .replace(/[.:;,\s]+$/, '');
}

export function upperFirst(s: string): string {
  return s ? s[0]!.toUpperCase() + s.slice(1) : s;
}

/** true, если в строке есть буквы и все они прописные. */
export function isAllCaps(s: string): boolean {
  const letters = s.replace(/[^\p{L}]/gu, '');
  return letters.length > 0 && letters === letters.toUpperCase() && letters !== letters.toLowerCase();
}

/** Первая буква строки прописная (строки без букв считаются корректными). */
export function startsWithUpper(s: string): boolean {
  const m = /\p{L}/u.exec(s);
  if (!m) return true;
  const ch = m[0];
  return ch === ch.toUpperCase() && ch !== ch.toLowerCase();
}

export function truncate(s: string, max = 80): string {
  const t = collapseSpaces(s.replace(/\ufffc/g, ''));
  return t.length > max ? t.slice(0, max - 1).trimEnd() + '…' : t;
}

/** Форма слова по числу: plural(3, ['абзац', 'абзаца', 'абзацев']) → «абзаца». */
export function plural(n: number, forms: readonly [string, string, string]): string {
  const n10 = Math.abs(n) % 10;
  const n100 = Math.abs(n) % 100;
  if (n10 === 1 && n100 !== 11) return forms[0];
  if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return forms[1];
  return forms[2];
}

export const countOf = (n: number, forms: readonly [string, string, string]): string =>
  `${n} ${plural(n, forms)}`;

/** Буквы обозначения приложений по ГОСТ 7.32-2017, п. 6.17.4: кириллица без Ё, З, Й, О, Ч, Ъ, Ы, Ь. */
export const APPENDIX_LETTERS_CYR = 'АБВГДЕЖИКЛМНПРСТУФХЦШЩЭЮЯ'.split('');
/** Латиница без I и O (там же). */
export const APPENDIX_LETTERS_LAT = 'ABCDEFGHJKLMNPQRSTUVWXYZ'.split('');

export function toRoman(n: number): string {
  if (n <= 0 || n >= 4000) return String(n);
  const map: Array<[number, string]> = [
    [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'], [100, 'C'], [90, 'XC'],
    [50, 'L'], [40, 'XL'], [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
  ];
  let out = '';
  for (const [v, s] of map) {
    while (n >= v) {
      out += s;
      n -= v;
    }
  }
  return out;
}

/** Буквенная нумерация в стиле Word: 1 → a, 26 → z, 27 → aa. */
export function toLetters(n: number, alphabet: string): string {
  if (n <= 0) return String(n);
  const len = alphabet.length;
  return alphabet[(n - 1) % len]!.repeat(Math.floor((n - 1) / len) + 1);
}

export const LATIN_ALPHABET = 'abcdefghijklmnopqrstuvwxyz';
/** Алфавит нумерации russianLower/russianUpper в Word. */
export const RUSSIAN_ALPHABET = 'абвгдежзиклмнопрстуфхцчшщэюя';

/** Пробелы, которые должны быть неразрывными: «10 мм», «А. А. Смирнов», «Смирнов А. А.», «№ 5», «с. 15», «т. е.». */
const NBSP_PLACES: readonly RegExp[] = [
  /(?<=\d) (?=(?:мм|см|дм|км|мкм|нм|м|кг|мг|г|л|мл|мс|мкс|нс|с|мин|ч|сут|кВ|мВ|мкВ|В|мА|мкА|А|кВт|МВт|мВт|Вт|кГц|МГц|ГГц|Гц|кОм|МОм|Ом|мкФ|нФ|пФ|Ф|мГн|мкГн|Гн|кДж|Дж|кПа|МПа|Па|Кбит|Мбит|Гбит|бит|Кбайт|Мбайт|Гбайт|байт|КБ|МБ|ГБ|ТБ|дБм|дБ|°C|°С|%|руб\.|р\.|коп\.|тыс\.|млн|млрд|шт\.|пт)(?![\p{L}\d]))/gu,
  /(?<=(?<!\p{L})[А-ЯЁ]\.) (?=[А-ЯЁ]\.)/gu,
  // Инициалы перед фамилией: «А. А. Смирнов». В «Смирнов А. А. Базы данных» после инициалов идёт заглавие — не трогаем.
  /(?<![А-ЯЁ][а-яё]+\s(?:[А-ЯЁ]\.\s?)?[А-ЯЁ]\.)(?<=(?<!\p{L})[А-ЯЁ]\.) (?=[А-ЯЁ][а-яё]{2,})/gu,
  /(?<=[А-ЯЁ][а-яё]{2,}) (?=[А-ЯЁ]\.\s?[А-ЯЁ]\.)/gu,
  /(?<=[№§]) (?=\d)/gu,
  /(?<=(?<!\p{L})(?:с|стр|рис|табл|гл|п|пп|разд|ч|вып|кн)\.) (?=\d)/gu,
  /(?<=(?<!\p{L})т\.) (?=[едпкн]\.)/gu,
];

/** Позиции обычных пробелов, которые по правилам набора должны быть неразрывными. */
export function nbspSpots(text: string): number[] {
  const spots = new Set<number>();
  for (const re of NBSP_PLACES) for (const m of text.matchAll(re)) spots.add(m.index);
  return [...spots].sort((a, b) => a - b);
}

export function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
